class Serializer {
  static #_NODE_TYPES = new Set(['query', 'and', 'or', 'not', 'clause']);
  static #_VALUE_TYPES = new Set(['word', 'phrase', 'range', 'missing']);
  static #_WORD_ESCAPE_RE = /[\s":,()[\]{}\\*?~^!]/;

  static serialize(node) {
    if (node === null || typeof node !== 'object') {
      throw new Error('INVALID_AST');
    }
    if (node.type === 'query') {
      if (node.child === null) {
        return '';
      }
      return Serializer.#_serializeNode(node.child);
    }
    if (Serializer.#_NODE_TYPES.has(node.type)) {
      return Serializer.#_serializeNode(node);
    }
    if (Serializer.#_VALUE_TYPES.has(node.type)) {
      return Serializer.#_serializeValue(node);
    }
    throw new Error('INVALID_AST');
  }

  static #_serializeNode(node) {
    if (node.type === 'and') {
      return Serializer.#_serializeBoolean(node, ' ');
    }
    if (node.type === 'or') {
      return Serializer.#_serializeOr(node);
    }
    if (node.type === 'not') {
      return `NOT ${Serializer.#_serializeOperand(node.child)}`;
    }
    if (node.type === 'clause') {
      return Serializer.#_serializeClause(node);
    }
    throw new Error('INVALID_AST');
  }

  static #_serializeBoolean(node, joiner) {
    if (!Array.isArray(node.children) || node.children.length < 2) {
      throw new Error('INVALID_AST');
    }
    const parts = node.children.map((child) => Serializer.#_serializeOperand(child));
    return parts.join(joiner);
  }

  static #_serializeOr(node) {
    if (Serializer.#_isCommaSugarable(node)) {
      const field = Serializer.#_escapeWord(node.children[0].field);
      const values = node.children.map((child) => Serializer.#_serializeValue(child.value));
      return `${field}:${values.join(',')}`;
    }
    return Serializer.#_serializeBoolean(node, ' OR ');
  }

  static #_isCommaSugarable(node) {
    if (node.commaSugar !== true) {
      return false;
    }
    const field = node.children[0]?.field;
    if (typeof field !== 'string') {
      return false;
    }
    return node.children.every((child) => {
      if (child.type !== 'clause' || child.field !== field) {
        return false;
      }
      return child.value.type === 'word' || child.value.type === 'phrase';
    });
  }

  static #_serializeOperand(child) {
    if (child === null || typeof child !== 'object') {
      throw new Error('INVALID_AST');
    }
    if (child.type === 'and') {
      return `(${Serializer.#_serializeBoolean(child, ' AND ')})`;
    }
    if (child.type === 'or' && !Serializer.#_isCommaSugarable(child)) {
      return `(${Serializer.#_serializeNode(child)})`;
    }
    return Serializer.#_serializeNode(child);
  }

  static #_serializeClause(node) {
    if (!Serializer.#_VALUE_TYPES.has(node.value?.type)) {
      throw new Error('INVALID_AST');
    }
    const value = Serializer.#_serializeValue(node.value);
    if (node.field === null) {
      return value;
    }
    if (typeof node.field !== 'string') {
      throw new Error('INVALID_AST');
    }
    return `${Serializer.#_escapeWord(node.field)}:${value}`;
  }

  static #_serializeValue(value) {
    if (value.type === 'missing') {
      return '';
    }
    if (value.type === 'word') {
      if (value.wildcard === true) {
        return Serializer.#_escapeWord(value.text, true);
      }
      return Serializer.#_renderText(value.text);
    }
    if (value.type === 'phrase') {
      return Serializer.#_renderText(value.text);
    }
    return Serializer.#_serializeRange(value);
  }

  static #_serializeRange(range) {
    let open = '{';
    if (range.includeLower) {
      open = '[';
    }
    let close = '}';
    if (range.includeUpper) {
      close = ']';
    }
    return `${open}${Serializer.#_serializeBound(range.lower)} TO ${Serializer.#_serializeBound(range.upper)}${close}`;
  }

  static #_serializeBound(bound) {
    if (bound === null) {
      return '*';
    }
    return Serializer.#_renderText(bound.text);
  }

  static #_renderText(text) {
    if (text === '') {
      return '""';
    }
    if (Serializer.#_WORD_ESCAPE_RE.test(text)) {
      return `"${text.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`;
    }
    return text;
  }

  static #_escapeWord(text, keepWildcards = false) {
    let escaped = '';
    for (const char of text) {
      if (keepWildcards && (char === '*' || char === '?')) {
        escaped += char;
        continue;
      }
      if (Serializer.#_WORD_ESCAPE_RE.test(char)) {
        escaped += `\\${char}`;
        continue;
      }
      escaped += char;
    }
    return escaped;
  }
}

export { Serializer };
export default Serializer;
